import { useCallback, useState } from "react";
import type { DocumentNode } from "@apollo/client";
import type { SortingState } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import Icon from "@/components/Icon";
import Table, {
  TableFooter,
  TableHeading,
  TableControls,
  toTableControlActions,
  buildActionColumn,
  buildExpandableCountColumn,
  type TableAction,
  type TableHeadingTopSpacing
} from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import Tooltip from "@/components/Tooltip";
import NotesTable from "@/features/global/components/NotesTable";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { isInFuture } from "@/lib/dateHelpers";
import { sanitizeHtmlId } from "@/lib/htmlHelpers";
import type { Band, CRMNote } from "@/types";
import { NoteNotableValues as NoteNotableValueEnum } from "@/types";
import { formatMembershipHistoryTableData, isMembershipBandEnded, type MembershipHistoryRow } from "./utils";

export interface MembershipHistoryTableActions {
  canAddNote?: boolean;
  canEditMembership?: boolean;
  canDeleteMembership?: boolean;
  onAddNote?: (bandId: string) => void;
  onEditMembership?: (row: MembershipHistoryRow) => void;
  onDeleteMembership?: (bandId: string) => void;
}

export interface MembershipHistoryTableProps {
  bands: Band[];
  loading: boolean;
  pgaId: string;
  primaryBandId?: string | null;
  refetchQueries: DocumentNode[];
  actions?: MembershipHistoryTableActions;
  headingActions?: TableAction[];
  showHeading?: boolean;
  headingTopSpacing?: TableHeadingTopSpacing;
  highlightBandId?: string | null;
  highlightNoteId?: string | null;
  tableRef?: React.Ref<HTMLDivElement>;
}

export default function MembershipHistoryTable({
  bands,
  loading,
  pgaId,
  primaryBandId,
  refetchQueries,
  actions,
  headingActions = [],
  showHeading = true,
  highlightBandId,
  highlightNoteId,
  tableRef
}: MembershipHistoryTableProps) {
  const { Menu: RowMenu, setMenu } = useTableRowMenu();
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "endDate", desc: true }]);

  const tableData = bands.length ? formatMembershipHistoryTableData(bands) : [];
  const hasRowActions = !!actions?.canAddNote || !!actions?.canEditMembership || !!actions?.canDeleteMembership;

  const renderNotesPanel = useCallback(
    (row: { original: MembershipHistoryRow }) => {
      const notes = (row.original.notes || []) as CRMNote[];
      if (notes.length === 0) {
        return <></>;
      }

      return (
        <NotesTable
          variant="details-panel"
          data={notes}
          title={`Notes (${notes.length})`}
          highlightNoteId={highlightNoteId}
          noteEditing={{
            enabled: !!actions?.canAddNote,
            notableId: row.original.id,
            notableType: NoteNotableValueEnum.MEMBERSHIP_BAND,
            refetchQueries,
            pgaId,
            objectType: "person"
          }}
        />
      );
    },
    [actions?.canAddNote, highlightNoteId, pgaId, refetchQueries]
  );

  return (
    <>
      <div ref={tableRef}>
        {showHeading ? (
          <TableHeading
            heading="Membership History"
            body="View membership history including changes to program and classification below"
            actions={headingActions}
          />
        ) : (
          <div className="pt-12" />
        )}
        <Table
          data={tableData}
          loading={loading}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          getRowCanExpand={(row) => (row.original.notes?.length || 0) > 0}
          renderExpandedRow={({ row }) => renderNotesPanel(row)}
          renderControls={
            !showHeading && headingActions.length > 0
              ? (_table, { onSortClick } = {}) => (
                  <TableControls actions={toTableControlActions(headingActions)} onSortClick={onSortClick} />
                )
              : undefined
          }
          columns={[
            {
              size: 250,
              header: "Section",
              id: "section",
              meta: { headerClassName: "hide-header-in-column" },
              cell: ({ row }) => (
                <div className="flex items-center gap-2">
                  {row.original.isPrimarySectionAffiliation && row.original.section && (
                    <Tooltip
                      variant="plain"
                      supportingText="Primary"
                      color="black"
                      menuCorner="end-start"
                      anchorCorner="start-start"
                    >
                      <Icon name="check" size={24} />
                    </Tooltip>
                  )}

                  {renderEmptyCellState(row.original.section)}
                </div>
              )
            },
            {
              accessorKey: "program",
              header: "Program",
              id: "program",
              enableSorting: false
            },
            {
              accessorKey: "classification",
              header: "Classification",
              id: "classification",
              enableSorting: false
            },
            {
              accessorKey: "startDate",
              header: "Start Date",
              id: "startDate",
              enableSorting: true,
              sortingFn: (rowA, rowB) => {
                const dateA = rowA.original.startDateRaw || "";
                const dateB = rowB.original.startDateRaw || "";
                return dateA.localeCompare(dateB);
              },
              cell: ({ row }) => {
                const startDateRaw = row.original.startDateRaw;
                const isFutureStartDate = startDateRaw && isInFuture(startDateRaw);

                return (
                  <div className="flex items-center gap-2">
                    {isFutureStartDate && (
                      <Tooltip
                        variant="plain"
                        supportingText="Start date is in the future"
                        color="black"
                        menuCorner="end-start"
                        anchorCorner="start-start"
                      >
                        <Icon name="emergency_home" size={20} className="text-extended-warning-brand-color" />
                      </Tooltip>
                    )}
                    {row.original.startDate}
                  </div>
                );
              }
            },
            {
              accessorKey: "endDate",
              header: "End Date",
              id: "endDate",
              enableSorting: true,
              sortingFn: (rowA, rowB) => {
                const dateA = rowA.original.endDateRaw || "";
                const dateB = rowB.original.endDateRaw || "";
                return dateA.localeCompare(dateB);
              }
            },
            {
              accessorKey: "startReason",
              header: "Start Reason",
              id: "startReason",
              enableSorting: false
            },
            {
              accessorKey: "terminationDate",
              header: "Termination Date",
              id: "terminationDate",
              enableSorting: true,
              sortingFn: (rowA: { original: MembershipHistoryRow }, rowB: { original: MembershipHistoryRow }) => {
                const dateA = rowA.original.terminatesOn || "";
                const dateB = rowB.original.terminatesOn || "";
                return dateA.localeCompare(dateB);
              }
            },
            {
              accessorKey: "terminationReason",
              header: "Termination Reason",
              id: "terminationReason",
              enableSorting: false
            },
            buildExpandableCountColumn<MembershipHistoryRow>({
              accessorKey: "notes",
              header: "Notes",
              getCount: (row) => row.notes?.length || 0,
              showExpandIcon: true,
              autoExpandRowId: highlightBandId
            }),
            ...(hasRowActions
              ? [
                  buildActionColumn<MembershipHistoryRow>({
                    getActions: (row) => {
                      const rowActions: Action[] = [];
                      const isPrimaryBand = row.id === primaryBandId;

                      if (actions?.canAddNote && actions.onAddNote) {
                        rowActions.push({
                          text: "Add Note",
                          onClick: () => actions.onAddNote?.(row.id)
                        });
                      }

                      if (actions?.canEditMembership && actions.onEditMembership) {
                        rowActions.push({
                          text: "Edit Membership",
                          onClick: () => actions.onEditMembership?.(row)
                        });
                      }

                      if (
                        actions?.canDeleteMembership &&
                        actions.onDeleteMembership &&
                        isMembershipBandEnded(row) &&
                        !isPrimaryBand
                      ) {
                        rowActions.push({
                          text: "Delete Membership",
                          iconName: "delete",
                          destructive: true,
                          onClick: () => actions.onDeleteMembership?.(row.id)
                        });
                      }

                      return rowActions;
                    },
                    actionId,
                    setMenu,
                    getRowId: (row) => sanitizeHtmlId(row.id)
                  })
                ]
              : [])
          ]}
          renderFooter={() => <TableFooter totalRowCount={tableData.length} />}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Membership History"
              caption="This person does not have any membership history"
              iconName="card_membership"
            />
          )}
          data-testid="membership-history-table"
        />
      </div>
      {hasRowActions && <RowMenu />}
    </>
  );
}
